/**
 * The document outline card.
 *
 * Renders the heading hierarchy the add-in read out of the document — never a
 * list the model retyped. The prompt used to ask for the whole outline twice
 * (once as markdown in the bubble, once again as the insert pill's payload); on
 * a 171-heading document that was ~5,000 tokens of transcription on the fast
 * tier and it stopped at #71, showing 71 of 171 headings with nothing to say
 * that anything was missing.
 *
 * The summary line above the list is the part a reader actually needs and the
 * part the model was never given: how many headings, how deep, and whether the
 * structure is recorded in paragraph STYLES or only in outline levels. A
 * document pasted in from HTML has real headings that no style records, which is
 * why its Navigation Pane, contents page and PDF bookmarks disagree — that is a
 * `/format` job, not a failed read, and saying so is more use than 171 bullets.
 * Disclosing the fallback follows G-9's rule for inferred results: label it.
 *
 * There is deliberately NO copy button here. The action bar below already
 * carries Variant B's `Insert outline (N) · Copy`, both derived from the same
 * sections, and a second Copy inside the card was the same action twice, forty
 * pixels apart.
 *
 * Heading text is rendered as a React child, never as HTML, so a heading
 * containing `<` or `&` shows those characters instead of being interpreted.
 */

import type { WordOutline } from "../types";
import styles from "./WordOutlineCard.module.css";

export interface WordOutlineCardProps {
  readonly outline: WordOutline;
}

/** Indent per level, in px. Depth is the ONLY visual difference between rows. */
const INDENT_STEP = 12;

/**
 * How the outline's structure is recorded, in one clause — or "" when there is
 * nothing worth saying.
 *
 * Only the mixed and style-less cases earn a mention. An ordinary document whose
 * headings all carry real styles is the expected case and needs no commentary.
 */
export function provenanceNote(styleBacked: number, outlineLevelOnly: number): string {
  if (outlineLevelOnly === 0) return "";
  if (styleBacked === 0) return "none carry Word heading styles";
  return `${outlineLevelOnly} without Word heading styles`;
}

/** "171 headings, 1 level deep · none carry Word heading styles · no contents page" */
export function summaryLine(outline: WordOutline): string {
  const { total, depth, styleBacked, outlineLevelOnly, hasToc } = outline;
  const parts = [`${total} heading${total === 1 ? "" : "s"}, ${depth} level${depth === 1 ? "" : "s"} deep`];
  const note = provenanceNote(styleBacked, outlineLevelOnly);
  if (note) parts.push(note);
  // null means the read was refused, which is NOT the same as "no contents page"
  // — saying the document has none off a failed read is the mistake to avoid.
  if (hasToc === false) parts.push("no contents page");
  if (hasToc === true) parts.push("has a contents page");
  return parts.join(" · ");
}

export function WordOutlineCard({ outline }: WordOutlineCardProps) {
  if (outline.rows.length === 0) return null;

  return (
    <section className={styles.card} aria-label={`Document outline, ${outline.total} headings`}>
      <h4 className={styles.heading}>Document outline</h4>
      <p className={styles.summary}>{summaryLine(outline)}</p>

      <ul className={styles.list}>
        {outline.rows.map((row, index) => (
          // Index-keyed on purpose: a document may legitimately repeat both a
          // heading's text AND its level (eighty-four schedules sharing section
          // names), so nothing else here is unique. The list is re-read whole
          // rather than reordered, so a positional key is stable enough.
          <li
            key={`${index}-${row.startParagraph}`}
            className={styles.row}
            style={{ paddingInlineStart: (row.level - 1) * INDENT_STEP }}
            data-testid={`outline-row-${index}`}
          >
            {row.text}
          </li>
        ))}
      </ul>
    </section>
  );
}
